import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Player } from '@lordicon/react';
import { Formik, Form, FormikProps } from 'formik';
import {
  useTranslator,
  useAppContext,
  formatIsoDate,
  formatIsoDateTime,
  useToast,
  callNativeMobileApp,
  withMobileAppMode,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button } from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import getAddMealValidationSchema from '../AddMeal/validationSchema';
import createValidationSchema from './mealValidationSchema';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { TrackerAddMealProps, Ingredient } from '../types';
import {
  buildIngredientFormValues,
  calculateUpdatedIngredients,
} from '../helpers/ingredientEditHelpers';
import { calculateMacrosFromIngredients } from '../helpers/mealCalculations';
import TrackerEditIngredientsForm from './TrackerEditIngredientsForm';
import {
  TrackerMealImage,
  TrackerMealMacroSummary,
  TrackerMealFormHeader,
  TrackerMealSaveAsUserMealButton,
  TrackerMealStickyFooter,
  TrackerMealRemoveModal,
} from './TrackerMeal';

interface AddIngredientReturnState {
  returnPath?: string;
  ingredients?: Ingredient[];
  mealName?: string;
  servings?: number;
  updatedAt?: number;
}

// const ADD_INGREDIENT_RETURN_STORAGE_KEY = 'tracker_add_ingredient_return_state';
const ADD_INGREDIENT_RETURN_EVENT = 'tracker_add_ingredient_return';

const TrackerAddMeal: React.FC<TrackerAddMealProps> = ({
  mealData,
  addMeal,
  addUserMeal,
  editMeal,
  removeMeal,
  saveAsUserMeal,
  mobileAppMode,
  date: dateProp,
  requestId,
  dietMealId,
  webviewModal,
  fromUserMeals,
}) => {
  const { t, language } = useTranslator();
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const lastAppliedUpdateRef = useRef<number | null>(null);
  /** Latest form values – used when deleting an ingredient so we sync edits into ingredients before recalc. */
  const formValuesRef = useRef<Record<string, unknown>>({});
  const formikRef = useRef<FormikProps<Record<string, unknown>> | null>(null);
  /** Last valid servings value - used to calculate ratio when servings change */
  const lastValidServingsRef = useRef<number>(mealData.portions);

  const isEditMode = Boolean(editMeal);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ingredients, setIngredients] = useState<Ingredient[]>(mealData.ingredients);
  const [lastIngredientsCount, setLastIngredientsCount] = useState(mealData.ingredients.length);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [isSavedToUserMeals, setIsSavedToUserMeals] = useState(mealData.isFromBaseMeal ?? false);
  const [isSavingToUserMeals, setIsSavingToUserMeals] = useState(false);
  const [hasIngredientsChanged, setHasIngredientsChanged] = useState(false);
  const [hasServingsChanged, setHasServingsChanged] = useState(false);
  /** Servings restored from add-ingredient return; when set, used in initialValues instead of mealData.portions. */
  const [currentServings, setCurrentServings] = useState<number | null>(null);
  /** Meal name restored from add-ingredient return; when set, used in initialValues instead of mealData.name. */
  const [currentMealName, setCurrentMealName] = useState<string | null>(null);
  const [customImageUrl, setCustomImageUrl] = useState<string | null>(null);

  const applyReturnState = useCallback(
    (state: AddIngredientReturnState | null) => {
      if (!state) {
        return;
      }
      // 1. Check if the return path is the same as the current path
      if (state.returnPath && state.returnPath !== location.pathname) {
        return;
      }
      // 2. protection against applying the same state again
      if (state.updatedAt != null && lastAppliedUpdateRef.current === state.updatedAt) {
        return;
      }
      // 3. update ingredients list + helper flags
      if (state.ingredients != null) {
        setIngredients(state.ingredients);
        setHasIngredientsChanged(true);
      }
      // 4. update the meal name if it comes in the package (state needed for enableReinitialize)
      if (state.mealName != null) {
        setCurrentMealName(state.mealName);
        formikRef.current?.setFieldValue('mealName', state.mealName);
      }
      // 5. restore servings so Formik reinit (from new ingredients) keeps user's value
      if (state.servings != null) {
        setCurrentServings(state.servings);
      }
      // 6. update the last applied update timestamp
      if (state.updatedAt != null) {
        lastAppliedUpdateRef.current = state.updatedAt;
      } else {
        lastAppliedUpdateRef.current = Date.now();
      }
    },
    [isEditMode, location.pathname]
  );

  // Handle return from add-ingredient: read sessionStorage on remount, and listen for custom event when component stays mounted (navigate(-1) cannot pass state).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<AddIngredientReturnState>;
      applyReturnState(customEvent.detail ?? null);
    };
    window.addEventListener(ADD_INGREDIENT_RETURN_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(ADD_INGREDIENT_RETURN_EVENT, handler as EventListener);
    };
  }, [applyReturnState]);

  // Pick up uploaded photo URL via custom event (when component stays mounted)
  useEffect(() => {
    const handlePhotoUploaded = (e: Event) => {
      const imageUrl = (e as CustomEvent<{ imageUrl: string }>).detail?.imageUrl;
      if (imageUrl) {
        setCustomImageUrl(imageUrl);
      }
    };

    window.addEventListener('activityPhotoUploaded', handlePhotoUploaded);
    return () => window.removeEventListener('activityPhotoUploaded', handlePhotoUploaded);
  }, []);

  const decimalSeparator = language === 'pl-PL' ? ',' : '.';

  const { date: dateFromParams } = useParams();
  const date = dateProp ?? dateFromParams;
  const [showDateInput, setShowDateInput] = useState(
    date && date !== formatIsoDate(new Date()) ? true : false
  );

  const isBarcodeRef = useRef(
    typeof window !== 'undefined' && window.location.search.includes('isBarcode=true')
  );

  const {
    consts: { APP_BRAND },
    images: { editPrimaryIcon, yourMealsAddIcon, yourMealsSavedIcon, cameraIcon },
    lotties: { trashIcon },
  } = useAppContext();

  const playerRefTrash = useRef<Player>(null);
  const iconsIconErrorColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-error-color')
      .trim();
    return color || '#000000';
  }, []);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minDateObj = new Date(today);
  minDateObj.setDate(minDateObj.getDate() - 30);
  const minDate = formatIsoDate(minDateObj);

  const maxDateObj = new Date(today);
  maxDateObj.setDate(maxDateObj.getDate() + 30);
  const maxDate = formatIsoDate(maxDateObj);

  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const handleDateInputBlur = (value: string | number) => {
    // If date is still today's date, hide the input and show "Today" div
    if (value === formatIsoDate(new Date())) {
      setShowDateInput(false);
    } else {
      setShowDateInput(true);
    }
  };

  const initialDate =
    isEditMode && mealData.loggedAt
      ? formatIsoDate(new Date(mealData.loggedAt))
      : date ?? formatIsoDate(new Date());
  const initialTime =
    isEditMode && mealData.loggedAt
      ? (() => {
          const match = mealData.loggedAt.match(/T(\d{2}):(\d{2})/);
          return match ? `${match[1]}:${match[2]}` : getCurrentTime();
        })()
      : getCurrentTime();

  const servingsForForm = currentServings ?? mealData.portions;

  // Aktualizuj ref ostatniej poprawnej wartości servings przy inicjalizacji i zmianie currentServings
  useEffect(() => {
    if (servingsForForm >= 0.1 && servingsForForm <= 30) {
      lastValidServingsRef.current = servingsForForm;
    }
  }, [servingsForForm]);

  const initialMealName = currentMealName ?? mealData.name;

  const initialValues = useMemo(() => {
    const base = fromUserMeals
      ? { servings: servingsForForm, saveYourMeal: false, mealName: initialMealName }
      : {
          date: initialDate,
          time: initialTime,
          servings: servingsForForm,
          saveYourMeal: false,
          mealName: initialMealName,
        };
    const ingredientValues = buildIngredientFormValues(
      ingredients,
      servingsForForm,
      initialMealName,
      isEditMode ? false : isBarcodeRef.current
    );
    // After first build, disable barcode logic so newly added ingredients are not nullified
    isBarcodeRef.current = false;
    if (ingredients.length === 0) {
      return base;
    }
    return {
      ...base,
      ...ingredientValues,
    };
  }, [
    fromUserMeals,
    initialDate,
    initialTime,
    servingsForForm,
    ingredients,
    initialMealName,
    isEditMode,
  ]);

  const combinedValidationSchema = useMemo(() => {
    const baseSchema = getAddMealValidationSchema();
    const schemaBase = fromUserMeals ? baseSchema.omit(['date', 'time', 'servings']) : baseSchema;
    if (isEditMode && ingredients.length === 0) return schemaBase;
    const ingredientSchema = createValidationSchema(ingredients);
    return schemaBase.shape(ingredientSchema.fields);
  }, [ingredients, isEditMode, fromUserMeals]);

  // When editing a meal logged on a date other than today, show the date input instead of "Today".
  useEffect(() => {
    if (isEditMode && initialDate !== formatIsoDate(new Date())) {
      setShowDateInput(true);
    }
  }, [isEditMode, initialDate]);

  const handleGoToTracker = (date: string) => {
    if (mobileAppMode) {
      callNativeMobileApp({
        action: 'open_modal',
        payload: {
          path: `/tracker/${date}`,
        },
      });
    } else {
      navigate(`/tracker/${date}`);
    }
  };

  const resetUnsavedChanges = () => {
    setHasIngredientsChanged(false);
    setHasServingsChanged(false);
    formikRef.current?.resetForm();
  };

  const handleSubmit = async (
    values: {
      date: string;
      time: string;
      servings: number;
      saveYourMeal: boolean;
      mealName: string;
      [key: string]: string | number | boolean | undefined;
    },
    formDirty?: boolean
  ) => {
    if (!values.mealName || values.mealName.trim() === '') {
      formikRef.current?.setFieldError('mealName', 'tracker.logMeal.input.validationError.empty');
      formikRef.current?.setFieldTouched('mealName', true, false);
      return;
    }

    setIsSubmitting(true);

    const mapUnitToApiFormat = (unit: string): string => {
      const unitMap: { [key: string]: string } = {
        g: 'grams',
        ml: 'milliliters',
        'fl oz': 'fl_oz',
        cl: 'cl',
      };
      return unitMap[unit] || unit;
    };

    const ingredientsToSubmit = Object.keys(values).some((k) => k.includes('_unit'))
      ? calculateUpdatedIngredients(
          ingredients,
          values as { [key: string]: number | string },
          values.servings
        )
      : ingredients;

    const requestIngredients = ingredientsToSubmit.map((ingredient: Ingredient) => {
      const unit =
        ingredient.measurements.length > 1
          ? ingredient.measurements[1].unit
          : ingredient.measurements[0].unit;

      const requestIngredient: {
        name: string;
        brand?: string;
        baseAmount: number;
        baseUnit: string;
        calories: number;
        proteinGrams: number | undefined;
        fatGrams: number | undefined;
        carbsGrams: number | undefined;
        measureId?: number;
        measureAmount?: number;
      } = {
        name: ingredient.name,
        brand: ingredient.brand,
        baseAmount:
          ingredient.measurements.length > 1
            ? ingredient.measurements[1].amount
            : ingredient.measurements[0].amount,
        baseUnit: mapUnitToApiFormat(unit),
        calories: Math.round(ingredient.kcal),
        proteinGrams: ingredient.macro?.protein.grams,
        fatGrams: ingredient.macro?.fat.grams,
        carbsGrams: ingredient.macro?.carbohydrates.grams,
      };

      if (ingredient.measurements.length > 1) {
        requestIngredient.measureId = ingredient.measurements[0].measureId;
        requestIngredient.measureAmount = ingredient.measurements[0].amount;
      }

      return requestIngredient;
    });

    const dateForRefresh = fromUserMeals ? formatIsoDate(new Date()) : (values.date as string);
    const portionsValue = +(values.servings ?? mealData.portions);

    const requestBase = {
      mealName: values.mealName as string,
      portions: portionsValue,
      ingredients: requestIngredients,
      sourceType: mealData.sourceType,
      imageUrl: customImageUrl || mealData.preparationImageUrl,
      mealId: dietMealId ? +dietMealId : undefined,
    };

    const request = fromUserMeals
      ? requestBase
      : {
          ...requestBase,
          dateTime: formatIsoDateTime(values.date as string, `${values.time}:00`),
        };

    const requestUserMeal = {
      dateTime: fromUserMeals
        ? formatIsoDateTime(formatIsoDate(new Date()), `${getCurrentTime()}:00`)
        : formatIsoDateTime(values.date as string, `${values.time}:00`),
      baseMealId: mealData.id,
      portions: portionsValue,
    };

    if (isEditMode && editMeal && mealData.id != null) {
      const { sourceType, ...requestWithoutSourceType } = request;
      const editRequest = { ...requestWithoutSourceType, mealId: mealData.id };
      try {
        const response = await editMeal(editRequest);
        if (response?.__typename === 'DailyTrackerEditMealSuccess') {
          callNativeMobileApp({
            action: 'refresh_data',
            payload: { module: 'tracker', date: dateForRefresh },
          });
          callNativeMobileApp({
            action: 'refresh_data',
            payload: { module: 'tracker', date: date },
          });
          resetUnsavedChanges();
          navigate(-Number(sessionStorage.getItem('tracker_depth')));
          toastSuccess(<p>{t('meals.notification.editMealConfirmation.content')}</p>);
        } else {
          toastError();
        }
      } catch (error) {
        toastError();
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const hasFormChanges = Boolean(formDirty) || hasIngredientsChanged || hasServingsChanged;

    if (!addUserMeal || hasFormChanges) {
      try {
        const addMealResponse = await addMeal?.(request, values.saveYourMeal);

        if (addMealResponse?.__typename === 'DailyTrackerAddMealSuccess') {
          const mealId = (addMealResponse as { meal?: { id?: number } })?.meal?.id;

          // save as user meal if checkbox is checked
          if (values.saveYourMeal && mealId) {
            console.log('Saving as user meal');
            try {
              console.log('Saving as user meal', {
                loggedMealId: mealId,
              });
              const saveAsUserMealResponse = await saveAsUserMeal?.({
                loggedMealId: mealId,
              });

              console.log('Save as user meal response', saveAsUserMealResponse);

              if (saveAsUserMealResponse?.__typename === 'DailyTrackerSaveAsUserMealSuccess') {
                console.log('User meal saved successfully');
              } else {
                console.error('Failed to save as user meal:', saveAsUserMealResponse);
              }
            } catch (error) {
              console.error('Error saving as user meal:', error);
            }
          }

          // refresh native tracker data
          callNativeMobileApp({
            action: 'refresh_data',
            payload: {
              module: 'tracker',
              date: dateForRefresh,
            },
          });

          if (webviewModal && mobileAppMode) {
            setTimeout(() => {
              callNativeMobileApp({
                action: 'show_toast',
                payload: {
                  type: 'success',
                  content: fromUserMeals
                    ? `${t('yourMeal.notification.savedInBase.content')}`
                    : `${t('meals.notification.trackerConfirmation.content')}`,
                  // buttonText: `${t('meals.notification.trackerConfirmation.button')}`,
                  // buttonUrl: `/tracker/${values.date}`,
                },
              });
              callNativeMobileApp({
                action: 'close_modal',
              });
            }, 0);
          } else {
            resetUnsavedChanges();
            navigate(-Number(sessionStorage.getItem('tracker_depth')));

            if (fromUserMeals) {
              toastSuccess(
                <p className="mb-0 w-100">{t('yourMeal.notification.savedInBase.content')}</p>
              );
            } else {
              toastSuccess(
                <p className="mb-0 w-100">
                  {t('meals.notification.trackerConfirmation.content')}
                  <Button color="link" onClick={() => handleGoToTracker(dateForRefresh)}>
                    {t('meals.notification.trackerConfirmation.button')}
                  </Button>
                </p>
              );
            }
          }
        } else {
          toastError();
        }
      } catch (error) {
        toastError();
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // add user meal
      try {
        const addUserMealResponse = await addUserMeal?.(requestUserMeal);

        if (addUserMealResponse?.__typename === 'DailyTrackerAddUserMealSuccess') {
          // refresh native tracker data
          callNativeMobileApp({
            action: 'refresh_data',
            payload: {
              module: 'tracker',
              date: dateForRefresh,
            },
          });

          resetUnsavedChanges();
          navigate(-Number(sessionStorage.getItem('tracker_depth')));
          toastSuccess(
            <p className="mb-0 w-100">
              {t('meals.notification.trackerConfirmation.content')}

              <Button color="link" onClick={() => handleGoToTracker(dateForRefresh)}>
                {t('meals.notification.trackerConfirmation.button')}
              </Button>
            </p>
          );
        } else {
          toastError();
        }
      } catch (error) {
        toastError();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const fakeSubmit = () => {
    inputRef.current?.blur();
  };

  /**
   * When servings change, rescale all ingredient amount fields and sync ingredients state.
   * We must update ingredients state with new per-portion amounts so that when Formik
   * reinitializes (enableReinitialize), initialValues are built from current amounts,
   * not from the original base amounts (which would overwrite user edits).
   */
  const handleServingsChange = useCallback(
    (
      newServings: number,
      currentServings: number,
      currentValues: { [key: string]: number | string | null },
      setFieldValue: (field: string, value: number) => void
    ) => {
      if (currentServings === newServings || currentServings <= 0) return;
      const ratio = newServings / currentServings;
      const newFormValues: { [key: string]: number | string } = {
        ...currentValues,
        servings: newServings,
      } as { [key: string]: number | string };
      ingredients.forEach((ing) => {
        const v = currentValues[ing.id];
        if (typeof v === 'number') {
          const rescaled = Math.round(v * ratio * 100) / 100;
          setFieldValue(ing.id, rescaled);
          newFormValues[ing.id] = rescaled;
        }
      });
      const updatedIngredients = calculateUpdatedIngredients(
        ingredients,
        newFormValues,
        newServings
      );
      setIngredients(updatedIngredients);
    },
    [ingredients]
  );

  const handleDeleteIngredient = (id: string) => {
    const currentValues = formValuesRef.current as { [key: string]: number | string };
    const servings = getSafeServings(currentValues.servings);
    const hasAmounts = Object.keys(currentValues).some((k) => k.includes('_unit'));
    const synced = hasAmounts
      ? calculateUpdatedIngredients(ingredients, currentValues, servings)
      : ingredients;
    setIngredients(synced.filter((i) => i.id !== id));
    //if (isEditMode) {
    setHasIngredientsChanged(true);
    //}

    // const deletedIngredient = synced.find((i) => i.id === id);

    // toastSuccess(
    //   <p className="w-100">
    //     {t('tracker.logMeal.ingredients.deleteConfirmation.description')}

    //     <Button
    //       color="link"
    //       className="float-end"
    //       onClick={() => handleAddIngredientBack(deletedIngredient as Ingredient)}
    //     >
    //       {t('tracker.logMeal.ingredients.deleteConfirmation.button')}
    //     </Button>
    //   </p>
    // );
  };

  // const handleAddIngredientBack = (deletedIngredient: Ingredient) => {
  //   setIngredients((prevIngredients) => {
  //     // Prevent adding the same ingredient twice
  //     if (prevIngredients.some((ing) => ing.id === deletedIngredient.id)) {
  //       return prevIngredients;
  //     }
  //     return prevIngredients.concat(deletedIngredient);
  //   });
  //   //if (isEditMode) {
  //   setHasIngredientsChanged(true);
  //   //}
  // };

  const handleAddIngredientClick = (values: { [key: string]: number | string }) => {
    const updatedIngredients = calculateUpdatedIngredients(
      ingredients,
      values,
      values.servings as number
    );
    navigate('add-ingredient', {
      state: {
        ingredients: updatedIngredients,
        mealName: values.mealName as string,
        returnPath: location.pathname,
        dietMealId,
        servings: values.servings as number,
      },
    });
  };

  const removeMealHandler = async () => {
    setShowAlertModal(false);
    if (!removeMeal) {
      toastError();
      return;
    }
    const response = await removeMeal(mealData.id);
    if (response?.code === 200) {
      callNativeMobileApp({
        action: 'refresh_data',
        payload: { module: 'tracker', date },
      });
      navigate(-1);
    } else {
      toastError();
    }
  };

  const saveAsUserMealHandler = async () => {
    if (isSavedToUserMeals) return null;
    setIsSavingToUserMeals(true);
    try {
      const response = await saveAsUserMeal?.({ loggedMealId: mealData.id });
      if (response?.__typename === 'DailyTrackerSaveAsUserMealSuccess') {
        setIsSavedToUserMeals(true);
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      setIsSavingToUserMeals(false);
    }
  };

  /** Returns a valid servings number in [0.1, 30] for display/calculations so invalid input never crashes children. */
  const getSafeServings = useCallback(
    (v: unknown): number => {
      const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
      if (Number.isFinite(n) && n >= 0.1 && n <= 30) return n;
      const fallback = mealData.portions;
      if (Number.isFinite(fallback) && fallback >= 0.1 && fallback <= 30) return fallback;
      return 1;
    },
    [mealData.portions]
  );

  const title = () => {
    if (isEditMode) {
      return t('tracker.mealDetails.navbarTitle');
    } else if (fromUserMeals) {
      return t('tracker.mealDetails.navbarTitle');
    } else {
      return t('tracker.logMeal.navbarTitle');
    }
  };

  return (
    <>
      <Layout>
        <TitleBar showBackBtn={true}>{title()}</TitleBar>

        <Formik
          innerRef={formikRef}
          onSubmit={fakeSubmit}
          initialValues={initialValues}
          validationSchema={combinedValidationSchema}
          validateOnChange={true}
          enableReinitialize
        >
          {({ isValid, values, setFieldValue, dirty, validateField }) => {
            formValuesRef.current = values as Record<string, unknown>;
            const formValues = values as Record<string, unknown>;
            // Aktualizuj ostatnią poprawną wartość servings jeśli jest poprawna
            const servingsValue = values.servings;
            if (
              typeof servingsValue === 'number' &&
              Number.isFinite(servingsValue) &&
              servingsValue >= 0.1 &&
              servingsValue <= 30
            ) {
              lastValidServingsRef.current = servingsValue;
            }
            const allIngredientFieldsValid =
              ingredients.length > 0 &&
              ingredients.every((ing) => {
                const v = formValues[ing.id];
                return v !== null && v !== undefined;
              });

            const isFormValid = isValid && allIngredientFieldsValid;

            // Calculate meal data directly from form values
            const currentServings = getSafeServings(values.servings);
            const hasAmounts = Object.keys(formValues).some((k) => k.includes('_unit'));
            const updatedIngredients = hasAmounts
              ? calculateUpdatedIngredients(
                  ingredients,
                  formValues as { [key: string]: number | string },
                  currentServings
                )
              : ingredients;
            const calculatedMealData = calculateMacrosFromIngredients(updatedIngredients);

            return (
              <Form>
                <div className="page-template-tracker">
                  <Container className="py-md-4 full-height4">
                    <Row>
                      <Col xl={{ size: 10, offset: 1 }}>
                        <div className="page-container">
                          <div className="tracker-container">
                            <Row>
                              <Col md="6">
                                <TrackerMealImage
                                  mealData={mealData}
                                  appBrand={APP_BRAND as string}
                                  customImageUrl={customImageUrl}
                                  cameraIcon={cameraIcon}
                                  onCameraClick={() => navigate('/tracker/upload-image')}
                                />
                              </Col>
                              <Col md="6">
                                <TrackerMealFormHeader
                                  showDateInput={showDateInput}
                                  minDate={minDate}
                                  maxDate={maxDate}
                                  mealName={(values.mealName as string) ?? ''}
                                  editPrimaryIcon={editPrimaryIcon}
                                  inputRef={inputRef}
                                  onDateBlur={handleDateInputBlur}
                                  showDateTimeInputs={!fromUserMeals}
                                  showServingsInput={!fromUserMeals}
                                  initialShowMealNameEdit={mealData.name === ''}
                                  onServingsChange={(newServings) => {
                                    if (
                                      !Number.isFinite(newServings) ||
                                      newServings < 0.1 ||
                                      newServings > 30
                                    )
                                      return;
                                    const current = lastValidServingsRef.current;
                                    if (current === newServings) return;
                                    //if (isEditMode) {
                                    setHasServingsChanged(true);
                                    //}
                                    setCurrentServings(newServings);
                                    handleServingsChange(
                                      newServings,
                                      current,
                                      values as unknown as {
                                        [key: string]: number | string | null;
                                      },
                                      setFieldValue
                                    );
                                    // Aktualizuj ostatnią poprawną wartość
                                    lastValidServingsRef.current = newServings;
                                    // Wywołaj walidację po aktualizacji wartości
                                    setTimeout(() => {
                                      validateField('servings');
                                    }, 0);
                                  }}
                                />
                                <TrackerMealMacroSummary
                                  calculatedMealData={calculatedMealData}
                                  servings={getSafeServings(values.servings)}
                                />
                              </Col>
                            </Row>
                          </div>
                        </div>

                        {isEditMode && (
                          <TrackerMealSaveAsUserMealButton
                            isSaved={isSavedToUserMeals}
                            isSaving={isSavingToUserMeals}
                            onClick={saveAsUserMealHandler}
                            savedIcon={yourMealsSavedIcon}
                            addIcon={yourMealsAddIcon}
                          />
                        )}

                        <TrackerEditIngredientsForm
                          ingredients={ingredients}
                          multiplier={getSafeServings(values.servings)}
                          values={values as unknown as { [key: string]: number | string | null }}
                          setFieldValue={setFieldValue}
                          onValuesChange={() => {
                            // Values change is handled by IngredientsSync component
                          }}
                          onDeleteIngredient={handleDeleteIngredient}
                          onAddIngredient={() =>
                            handleAddIngredientClick(
                              values as unknown as { [key: string]: number | string }
                            )
                          }
                          decimalSeparator={decimalSeparator}
                          lastIngredientsCount={lastIngredientsCount}
                          setLastIngredientsCount={setLastIngredientsCount}
                          requestId={requestId}
                          showAiSection={
                            Boolean(requestId) || mealData.sourceType === 'ai_description'
                          }
                        />

                        <Button
                          color="link"
                          className="w-100 mb-xl d-flex align-items-center justify-content-center"
                          onClick={() => {
                            if (isEditMode) {
                              setShowAlertModal(true);
                            } else {
                              if (webviewModal && mobileAppMode) {
                                setTimeout(() => {
                                  callNativeMobileApp({
                                    action: 'close_modal',
                                  });
                                }, 0);
                              } else {
                                navigate(-Number(sessionStorage.getItem('tracker_depth')));
                              }
                            }
                          }}
                          onMouseEnter={() => playerRefTrash.current?.playFromBeginning()}
                          onTouchStart={() => playerRefTrash.current?.playFromBeginning()}
                        >
                          <div className="me-1 d-inline-block">
                            <Player
                              ref={playerRefTrash}
                              icon={trashIcon}
                              size={20}
                              colorize={iconsIconErrorColor}
                            />
                          </div>
                          <span className="text-text-error-color">
                            {fromUserMeals ? t('yourMeal.removeButton') : t('tracker.deleteButton')}
                          </span>
                        </Button>
                      </Col>
                    </Row>
                  </Container>

                  {(!isEditMode ||
                    dirty ||
                    hasIngredientsChanged ||
                    hasServingsChanged ||
                    customImageUrl) && (
                    <TrackerMealStickyFooter
                      isEditMode={isEditMode}
                      hasAddUserMeal={Boolean(addUserMeal)}
                      fromUserMeals={fromUserMeals}
                      isSubmitting={isSubmitting}
                      isFormValid={isFormValid}
                      onSubmit={() =>
                        handleSubmit(
                          values as {
                            date: string;
                            time: string;
                            servings: number;
                            saveYourMeal: boolean;
                            mealName: string;
                            [key: string]: string | number | boolean | undefined;
                          },
                          dirty
                        )
                      }
                      submitValues={values as Record<string, unknown>}
                    />
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </Layout>

      <TrackerMealRemoveModal
        isOpen={showAlertModal}
        onToggle={() => setShowAlertModal(!showAlertModal)}
        onConfirm={removeMealHandler}
        t={t}
      />
    </>
  );
};

export default withMobileAppMode(TrackerAddMeal);
